import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles } from '../utils/refreshAnimation';
import { bankServiceRunService } from '../services/bankServiceRunService';
import type { SirketDto } from '../types/bankServiceRun';

const Sirketler = () => {
  const { selectedTenantIds, selectedServerIds, refreshTrigger, isRefreshing } = useFilter();
  const [items, setItems] = useState<SirketDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filterVkn, setFilterVkn] = useState('');
  const [filterUnvan, setFilterUnvan] = useState('');

  const selectedTenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : undefined;
  const selectedServerId = selectedServerIds.length === 1 ? selectedServerIds[0] : undefined;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await bankServiceRunService.getSirketlerPaged({
        tenantId: selectedTenantId,
        serverId: selectedServerId,
        vkn: filterVkn.trim() || undefined,
        unvan: filterUnvan.trim() || undefined,
        page: page + 1,
        pageSize,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(message || 'Şirketler yüklenirken bir hata oluştu');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, selectedServerId, filterVkn, filterUnvan, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshTrigger]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Şirketler
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              size="small"
              label="VKN"
              placeholder="VKN ile filtrele..."
              value={filterVkn}
              onChange={(e) => {
                setFilterVkn(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 180 }}
              inputProps={{ 'aria-label': 'VKN ile filtrele' }}
            />
            <TextField
              size="small"
              label="Ünvan"
              placeholder="Ünvan ile filtrele..."
              value={filterUnvan}
              onChange={(e) => {
                setFilterUnvan(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 220 }}
              inputProps={{ 'aria-label': 'Ünvan ile filtrele' }}
            />
          </Box>

          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderTop: 'none' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
                <CircularProgress />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      Sunucu
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      VKN
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      Ünvan
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {filterVkn || filterUnvan ? 'Filtreye uygun kayıt bulunamadı.' : 'Henüz şirket kaydı yok.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row, index) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                          '&:hover': { bgcolor: 'action.selected' },
                          transition: 'background-color 0.15s',
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{row.serverName ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{row.vkn}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{row.unvan || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangePageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Sayfa başına:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `~${to}`}`}
          />
        </Paper>
      </Box>
    </Layout>
  );
};

export default Sirketler;
